// The live inbox (brief 1B-6): while a screen that shows messages is open and the tab is visible,
// ask the server every 15 seconds for what changed since the last answer, and draw only that. The
// top bar's Inbox count updates the same way on every screen. Asking stops while the tab is hidden
// and asks once when it comes back. No outside service: the server reads what's already stored.
//
// One timer for the whole app. A screen watches with a key ("inbox", "messages:…") and a task; a
// task answering false is done (its screen went) and is dropped.

/** How often the app asks what changed. */
export const LIVE_EVERY_MS = 15_000;

/** What the loop needs from the browser (tests give their own). */
export interface LiveEnv {
  visible: () => boolean;
  setTimer: (fn: () => void, ms: number) => unknown;
  clearTimer: (handle: unknown) => void;
  /** Calls fn whenever the tab is hidden or shown again. */
  onVisibility: (fn: () => void) => void;
}

/** A screen's question; answering false means it is done (its screen went). */
export type LiveTask = () => Promise<unknown>;

export interface Live {
  /** Starts (or replaces) a task under this key. */
  watch(key: string, task: LiveTask): void;
  unwatch(key: string): void;
  has(key: string): boolean;
  /** Asks now, without waiting for the timer (after a send, say). */
  now(): Promise<void>;
}

export function createLive(env: LiveEnv, every = LIVE_EVERY_MS): Live {
  const tasks = new Map<string, LiveTask>();
  let timer: unknown = null;
  let running: Promise<void> | null = null;
  const stop = () => {
    if (timer !== null) env.clearTimer(timer);
    timer = null;
  };
  const plan = () => {
    stop();
    if (env.visible() && tasks.size) timer = env.setTimer(() => void run(), every);
  };
  async function ask() {
    for (const [key, task] of [...tasks]) {
      if (!env.visible()) return;
      try {
        if ((await task()) === false && tasks.get(key) === task) tasks.delete(key);
      } catch {
        // A failed answer is asked again next time.
      }
    }
  }
  function run(): Promise<void> {
    timer = null;
    if (!env.visible() || !tasks.size) return Promise.resolve();
    running ??= ask().finally(() => {
      running = null;
      plan();
    });
    return running;
  }
  env.onVisibility(() => {
    // Hidden: stop asking. Shown again: ask once now, then every 15 seconds.
    if (env.visible()) void run();
    else stop();
  });
  return {
    watch(key, task) {
      tasks.set(key, task);
      if (timer === null && !running) plan();
    },
    unwatch(key) {
      tasks.delete(key);
      if (!tasks.size) stop();
    },
    has: (key) => tasks.has(key),
    now: run,
  };
}

/** The app's one loop, on the browser's own clock and visibility. */
export const live: Live = createLive({
  visible: () => typeof document === "undefined" || document.visibilityState === "visible",
  setTimer: (fn, ms) => setTimeout(fn, ms),
  clearTimer: (h) => clearTimeout(h as ReturnType<typeof setTimeout>),
  onVisibility: (fn) => {
    if (typeof document !== "undefined") document.addEventListener("visibilitychange", fn);
  },
});

/** The inbox's counts, as the server answers them; the shell draws the badge and the left menu. */
export const COUNTS_EVENT = "fc:inbox-counts";
export const publishCounts = (counts: unknown) =>
  window.dispatchEvent(new CustomEvent(COUNTS_EVENT, { detail: counts }));
